import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { discountAPI } from '../../services/api';
import { useNotification } from '../../hooks/useNotification';

const DiscountForm = () => {
  const { t } = useTranslation();
  const { showError, showWarning } = useNotification();
  const { id } = useParams();
  const navigate = useNavigate();

  // Prevent scroll wheel from changing number input values
  const handleNumberInputWheel = (e) => {
    e.currentTarget.blur();
  };
  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    type: 'fixed',
    value: '',
    is_active: true,
    description: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (id) loadDiscount();
  }, [id]);

  const loadDiscount = async () => {
    try {
      const response = await discountAPI.getOne(id);
      const data = response.data.data || response.data;
      setFormData({
        name: data.name || '',
        name_ar: data.name_ar || '',
        type: data.type || 'fixed',
        value: data.value || '',
        is_active: data.is_active ?? true,
        description: data.description || '',
      });
    } catch (error) {
      showError(t('messages.error'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate percentage
    if (formData.type === 'percentage' && parseFloat(formData.value) > 100) {
      showWarning(t('discounts.percentageError'));
      return;
    }

    setLoading(true);

    try {
      if (id) {
        await discountAPI.update(id, formData);
      } else {
        await discountAPI.create(formData);
      }
      navigate('/discounts');
    } catch (error) {
      showError(t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{id ? t('discounts.editDiscountTitle') : t('discounts.addDiscountTitle')}</h2>
        <p className="text-gray-600 mt-1">{t('discounts.discountDetails')}</p>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('discounts.nameEnglish')}</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Summer Sale"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('discounts.nameArabic')}</label>
            <input
              type="text"
              value={formData.name_ar}
              onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., تخفيض الصيف"
              dir="rtl"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('discounts.typeLabel')}</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="fixed">{t('discounts.fixedAmount')}</option>
                <option value="percentage">{t('discounts.percentage')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('discounts.valueLabel')} {formData.type === 'percentage' && t('discounts.valuePercentageHint')}
              </label>
              <div className="relative">
                {formData.type === 'fixed' && (
                  <span className="absolute left-3 top-3 text-gray-500">{t('common.currency')}</span>
                )}
                <input
                  type="number"
                  step="0.01"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  onWheel={handleNumberInputWheel}
                  required
                  min="0"
                  max={formData.type === 'percentage' ? '100' : undefined}
                  className={`w-full ${formData.type === 'fixed' ? 'pl-16' : 'pl-4'} pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&]:[-moz-appearance:textfield]`}
                  placeholder={formData.type === 'percentage' ? '0-100' : '0.00'}
                />
                {formData.type === 'percentage' && (
                  <span className="absolute right-3 top-3 text-gray-500">%</span>
                )}
              </div>
              {formData.type === 'percentage' && parseFloat(formData.value) > 100 && (
                <p className="mt-1 text-sm text-red-600">{t('discounts.percentageExceedError')}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('discounts.descriptionLabel')}</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('discounts.optionalDescription')}
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
              {t('discounts.activeLabel')}
            </label>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-gray-700">
              <strong>{t('discounts.previewLabel')}</strong> {formData.name || 'Discount Name'} -{' '}
              {formData.type === 'percentage'
                ? `${formData.value || '0'}${t('discounts.percentOff')}`
                : `${t('common.currency')} ${parseFloat(formData.value || 0).toFixed(2)} ${t('discounts.currencyOff')}`
              }
            </p>
          </div>

          <div className="flex space-x-4 pt-4">
            <button
              type="submit"
              disabled={loading || (formData.type === 'percentage' && parseFloat(formData.value) > 100)}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('common.loading') : id ? t('discounts.updateButton') : t('discounts.createButton')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/discounts')}
              className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all duration-200 font-medium"
            >
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DiscountForm;
